var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Box, Table } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useMemo } from 'react';
import styles from './compareContactsTable.module.scss';
export const CompareContactsTable = (_a) => {
    var { contacts = [], previewContact } = _a, rest = __rest(_a, ["contacts", "previewContact"]);
    const normalizedContacts = useMemo(() => {
        return contacts.map((contact) => {
            var _a;
            return (Object.assign(Object.assign({}, contact), { company: (_a = contact.company) === null || _a === void 0 ? void 0 : _a.name }));
        });
    }, [contacts]);
    const normalizedPreviewContact = useMemo(() => {
        var _a;
        return Object.assign(Object.assign({}, previewContact), { company: (_a = previewContact.company) === null || _a === void 0 ? void 0 : _a.name });
    }, [previewContact]);
    const renderPropertyValue = useCallback((value, disabledValues) => {
        if (typeof value === 'undefined' || value === null)
            return null;
        if (Array.isArray(value)) {
            return value.map((val) => (_jsx(Box, Object.assign({ className: classnames({ [styles.tableListCell]: value.length > 1 }) }, { children: (disabledValues === null || disabledValues === void 0 ? void 0 : disabledValues.includes(val)) ? _jsx("s", { children: val }) : val }), val)));
        }
        return _jsx(_Fragment, { children: (disabledValues === null || disabledValues === void 0 ? void 0 : disabledValues.includes(value)) ? _jsx("s", { children: value }) : value });
    }, []);
    const renderProperty = useCallback((property) => {
        const previewValue = normalizedPreviewContact[property];
        return normalizedContacts.map((contact, i) => {
            var _a;
            const isValuesArrayType = Array.isArray(previewValue) && Array.isArray(contact[property]);
            const disabledValues = isValuesArrayType
                ? (_a = contact[property]) === null || _a === void 0 ? void 0 : _a.filter((value) => !previewValue.includes(value))
                : normalizedPreviewContact[property] !== contact[property]
                    ? [contact[property]]
                    : [];
            return (_jsx(Table.Td, { children: renderPropertyValue(contact[property], disabledValues) }, `table-td-${property}-${i}`));
        });
    }, [normalizedContacts, normalizedPreviewContact, renderPropertyValue]);
    const renderCombinedProperties = useCallback((properties) => {
        return normalizedContacts.map((contact, i) => {
            const values = properties
                .flatMap((property) => contact[property])
                .filter((value) => {
                return typeof value === 'string';
            });
            const disabledValues = properties.reduce((acc, property) => {
                var _a;
                const previewValue = normalizedPreviewContact[property];
                const isValuesArrayType = Array.isArray(previewValue) && Array.isArray(contact[property]);
                const disabledPropertyValues = isValuesArrayType
                    ? (_a = contact[property]) === null || _a === void 0 ? void 0 : _a.filter((value) => !previewValue.includes(value))
                    : normalizedPreviewContact[property] !== contact[property]
                        ? [contact[property]]
                        : [];
                disabledPropertyValues.forEach((value) => value !== null && acc.push(value));
                return acc;
            }, []);
            return (_jsx(Table.Td, { children: renderPropertyValue(values, disabledValues) }, `table-td-${properties.join('-')}-${i}`));
        });
    }, [normalizedContacts, normalizedPreviewContact, renderPropertyValue]);
    const renderAvatarProperty = useCallback(() => {
        return normalizedContacts.map((contact, i) => {
            return (_jsx(Table.Td, { children: !!contact.avatar_url && (_jsx(Box, Object.assign({ className: classnames(styles.avatarContainer, {
                        [styles.isForRemoval]: normalizedPreviewContact.avatar_url !== contact.avatar_url,
                    }) }, { children: _jsx(Avatar, { size: "24", user: {
                            avatar_url: contact.avatar_url,
                        }, className: styles.avatar }) }))) }, `table-td-${'avatar'}-${i}`));
        });
    }, [normalizedContacts, normalizedPreviewContact]);
    const hasProperty = useCallback((property) => {
        return normalizedContacts.some((contact) => !!contact[property]);
    }, [normalizedContacts]);
    return (_jsxs(Table, Object.assign({}, rest, { className: classnames(styles.compareTable, rest.className) }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup", width: "140px" }, { children: "Information" })), contacts.map((_, i) => (_jsxs(Table.Th, Object.assign({ className: i < contacts.length ? styles.th : '', width: "100%" }, { children: ["Contact ", i + 1] }), `table-head-th-${i}`)))] }) }), _jsxs(Table.Tbody, { children: [hasProperty('avatar_url') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "Avatar" })), renderAvatarProperty()] })), hasProperty('given_name') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "First name" })), renderProperty('given_name')] })), hasProperty('family_name') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "Last name" })), renderProperty('family_name')] })), hasProperty('name') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "Display name" })), renderProperty('name')] })), hasProperty('email') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "Email" })), renderCombinedProperties(['email', 'additional_emails'])] })), hasProperty('phone_number') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "Phone" })), renderCombinedProperties([
                                'phone_number',
                                'additional_phone_numbers',
                            ])] })), hasProperty('company') && (_jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ scope: "rowgroup" }, { children: "Company" })), renderProperty('company')] }))] })] })));
};
